<template>
  <v-container>
    <h3 class="page-title">帳號管理</h3>
    <v-row>
      <v-col cols="6" sm="3">
        <v-autocomplete
          v-model="行政區"
          :items="衛生所"
          label="負責衛生所"
          outlined
          dense
          hide-details
          :disabled="!!this.$store.state.user.行政區"
        ></v-autocomplete>
      </v-col>
      <v-col cols="6" sm="3">
        <v-text-field
          label="關鍵字"
          v-model="keyword"
          outlined
          dense
          hide-details
          clearable
        ></v-text-field>
      </v-col>

      <v-col class="text-right">
        <v-btn color="info" class="mr-3" @click="exportUsers">匯出 CSV</v-btn>
        <v-btn color="primary" @click="create">新增</v-btn>
      </v-col>
    </v-row>
    <div class="mt-3">
      <v-data-table
        :headers="headers"
        :items="filterUsers"
        no-data-text="尚無帳號"
        :mobile-breakpoint="0"
        :footer-props="{
          itemsPerPageText: '每頁幾筆',
          itemsPerPageOptions: [10, 30, -1],
        }"
      >
        <template v-slot:item.動作="{ item }">
          <v-btn small text color="primary" @click="update(item)">修改</v-btn>
        </template>
        <template v-slot:item.lastLogined="{ item }">
          <template v-if="item.lastLogined">
            {{ item.lastLogined }}
          </template>
          <template v-else> - </template>
        </template>
        <template v-slot:item.lastPasswordUpdated="{ item }">
          <span
            v-if="checkOverTime(item.lastPasswordUpdated)"
            class="red--text font-weight-medium"
          >
            {{ checkOverTime(item.lastPasswordUpdated) ? "過期" : "" }}</span
          >
          <span
            v-else
            :class="{
              'warning--text font-weight-medium': checkOverTime(
                item.lastPasswordUpdated,
                83
              ),
            }"
          >
            {{
              dayjs(item.lastPasswordUpdated)
                .add(90, "day")
                .format("YYYY-MM-DD")
            }}
          </span>
        </template>
        <template v-slot:item.enable="{ item }">
          <div
            class="red--text font-weight-medium"
            v-if="item.enable === false"
          >
            停用
          </div>
          <div v-else class="green--text font-weight-medium">啟用</div>
        </template>
      </v-data-table>
    </div>

    <v-divider class="my-6" v-if="canReviewApplications"></v-divider>
    <div v-if="canReviewApplications">
      <v-row align="center">
        <v-col>
          <h3 class="page-title mb-0">帳號申請審核</h3>
        </v-col>
        <v-col class="text-right">
          <v-btn small color="info" @click="loadApplications">重新整理</v-btn>
        </v-col>
      </v-row>
      <v-data-table
        :headers="applicationHeaders"
        :items="applications"
        no-data-text="目前沒有待審申請"
        :mobile-breakpoint="0"
        :footer-props="{
          itemsPerPageText: '每頁幾筆',
          itemsPerPageOptions: [10, 30, -1],
        }"
      >
        <template v-slot:item.status="{ item }">
          <span v-if="item.status === 'pending'" class="warning--text">待審核</span>
          <span v-else-if="item.status === 'approved'" class="green--text"
            >已核准</span
          >
          <span v-else class="red--text">已退件</span>
        </template>
        <template v-slot:item.動作="{ item }">
          <div>
            <v-btn small text color="info" @click="openDetailDialog(item)"
              >查看更多</v-btn
            >
            <template v-if="item.status === 'pending'">
              <v-btn small text color="success" @click="openApproveDialog(item)"
                >核准</v-btn
              >
              <v-btn small text color="error" @click="openRejectDialog(item)"
                >退件</v-btn
              >
            </template>
          </div>
        </template>
      </v-data-table>
    </div>

    <UserDialog
      v-if="dialog"
      v-model="dialog"
      :theUser="selected"
      @load="load"
    ></UserDialog>

    <v-dialog v-model="approveDialog" max-width="520">
      <v-card>
        <v-card-title>核准申請</v-card-title>
        <v-card-text>
          <v-text-field
            label="帳號"
            :value="reviewTarget.帳號 || ''"
            readonly
            outlined
            dense
          ></v-text-field>
          <v-select
            label="角色"
            v-model="reviewForm.role"
            :items="reviewRoles"
            outlined
            dense
          ></v-select>
          <v-text-field
            label="初始密碼"
            value="由申請者透過通知信設定"
            disabled
            outlined
            dense
          ></v-text-field>
          <v-textarea
            label="審核備註（選填）"
            v-model="reviewForm.reviewComment"
            rows="2"
            outlined
            dense
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="approveDialog = false">取消</v-btn>
          <v-btn color="success" @click="approveApplication">送出核准</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectDialog" max-width="520">
      <v-card>
        <v-card-title>退件申請</v-card-title>
        <v-card-text>
          <v-text-field
            label="帳號"
            :value="reviewTarget.帳號 || ''"
            readonly
            outlined
            dense
          ></v-text-field>
          <v-textarea
            label="退件原因（必填）"
            v-model="reviewForm.reviewComment"
            rows="3"
            outlined
            dense
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="rejectDialog = false">取消</v-btn>
          <v-btn color="error" @click="rejectApplication">確認退件</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailDialog" max-width="620">
      <v-card>
        <v-card-title>申請詳細資訊</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                label="申請時間"
                :value="detailTarget.createdAt || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="狀態"
                :value="statusText(detailTarget.status)"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="帳號"
                :value="detailTarget.帳號 || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="姓名"
                :value="detailTarget.姓名 || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="Email"
                :value="detailTarget.email || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="聯絡電話"
                :value="detailTarget.聯絡電話 || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="醫事單位"
                :value="detailTarget.醫事單位 || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="醫事機構代碼"
                :value="detailTarget.醫事機構代碼 || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="行政區"
                :value="detailTarget.行政區 || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="審核時間"
                :value="detailTarget.reviewedAt || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="審核人員"
                :value="detailTarget.reviewedBy || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                label="核准角色"
                :value="detailTarget.reviewedRole || '-'"
                readonly
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12">
              <v-textarea
                label="申請原因"
                :value="detailTarget.申請原因 || '-'"
                readonly
                outlined
                rows="2"
                dense
              ></v-textarea>
            </v-col>
            <v-col cols="12">
              <v-textarea
                label="審核備註"
                :value="detailTarget.reviewComment || '-'"
                readonly
                outlined
                rows="2"
                dense
              ></v-textarea>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="detailDialog = false">關閉</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import _ from "lodash";
import downloadCsv from "download-csv";
import util from "@/mixins/util";
import { 行政區 } from "@/libs/const";
import dayjs from "dayjs";
import UserDialog from "@/components/Account/userDialog";

export default {
  name: "Account",
  mixins: [util],
  components: {
    UserDialog,
  },
  data: () => ({
    行政區: null,
    keyword: null,
    headers: [
      { text: "行政區", value: "行政區" },
      { text: "帳號", value: "帳號" },
      { text: "角色", value: "角色" },
      { text: "醫事單位", value: "醫事單位" },
      { text: "最後登入", value: "lastLogined", align: "center" },
      { text: "密碼到期日", value: "lastPasswordUpdated", align: "center" },
      { text: "狀態", value: "enable" },
      { text: "", value: "動作", sortable: false },
    ],
    users: [],
    applications: [],
    applicationHeaders: [
      { text: "申請時間", value: "createdAt" },
      { text: "帳號", value: "帳號" },
      { text: "姓名", value: "姓名" },
      { text: "Email", value: "email" },
      { text: "聯絡電話", value: "聯絡電話" },
      { text: "醫事單位", value: "醫事單位" },
      { text: "行政區", value: "行政區" },
      { text: "狀態", value: "status" },
      { text: "", value: "動作", sortable: false },
    ],
    approveDialog: false,
    rejectDialog: false,
    detailDialog: false,
    reviewTarget: {},
    detailTarget: {},
    reviewForm: {
      role: "",
      reviewComment: "",
    },
    selected: null,
    dialog: false,
  }),
  computed: {
    filterUsers() {
      let users = this.users;
      if (this.行政區 && this.行政區.value) {
        users = users.filter((row) => this.行政區.value == row.行政區);
      }
      if (this.keyword) {
        users = users.filter(
          (row) =>
            row.帳號?.includes(this.keyword) ||
            row.角色?.includes(this.keyword) ||
            row.醫事單位?.includes(this.keyword)
        );
      }
      return users;
    },
    isManager() {
      return ["管理者", "衛生局管理員", "衛生局", "衛生所管理者"].includes(
        this.$store.state.user.角色
      );
    },
    isHospital() {
      return ["醫療院所"].includes(this.$store.state.user.角色);
    },
    canReviewApplications() {
      return ["管理者", "衛生局管理員"].includes(this.$store.state.user.角色);
    },
    reviewRoles() {
      switch (this.$store.state.user.角色) {
        case "管理者":
          return [
            "管理者",
            "衛生局管理員",
            "衛生局",
            "衛生所管理者",
            "衛生所",
            "醫療院所",
          ];
        case "衛生局管理員":
          return ["衛生局管理員", "衛生局", "衛生所管理者", "衛生所", "醫療院所"];
        default:
          return [];
      }
    },
    衛生所() {
      let place = 行政區.map((v) => ({
        text: v + "衛生所",
        value: v,
      }));
      place.unshift({
        text: "全部",
        value: null,
      });
      return place;
    },
  },
  async created() {
    if (this.$store.state.user.行政區) {
      this.行政區 = this.衛生所.find(
        (row) => row.value == this.$store.state.user.行政區
      );
    }
    await this.load();
    if (this.canReviewApplications) {
      await this.loadApplications();
    }
  },
  methods: {
    isStrongPassword(password) {
      if (!password || password.length < 8) return false;
      const hasLower = /[a-z]/.test(password);
      const hasUpper = /[A-Z]/.test(password);
      const hasDigit = /[0-9]/.test(password);
      const hasSpecial = /[!@#$%^&*()_+\-=\[\]{}~;':"\\|,.<>\/?]/.test(
        password
      );
      const categories = [hasLower, hasUpper, hasDigit, hasSpecial].filter(
        Boolean
      ).length;
      return categories >= 3;
    },
    statusText(status) {
      if (status === "pending") return "待審核";
      if (status === "approved") return "已核准";
      if (status === "rejected") return "已退件";
      return "-";
    },
    formatPasswordDueDate(lastPasswordUpdated) {
      if (!lastPasswordUpdated) {
        return "-";
      }
      return dayjs(lastPasswordUpdated).add(90, "day").format("YYYY-MM-DD");
    },
    exportUsers() {
      if (!this.users.length) {
        this.$toast.error("無可匯出帳號資料");
        return false;
      }

      const data = this.users.map((row) => ({
        行政區: row.行政區 || "-",
        帳號: row.帳號 || "-",
        角色: row.角色 || "-",
        醫事單位: row.醫事單位 || "-",
        最後登入: row.lastLogined || "-",
        密碼到期日: this.formatPasswordDueDate(row.lastPasswordUpdated),
        狀態: row.enable === false ? "停用" : "啟用",
      }));

      const filename = `帳號管理_全部帳號_${dayjs().format("YYYY-MM-DD")}`;
      downloadCsv(data, {}, filename);
    },
    async load() {
      let users = (await this.axios.get("/dashboard/user")).data;
      this.users = users;
    },
    async loadApplications() {
      if (!this.canReviewApplications) return;
      const rows = (await this.axios.get("/dashboard/user/applications", {
        params: { status: "pending" },
      })).data;
      this.applications = rows;
    },
    openApproveDialog(item) {
      this.reviewTarget = item;
      this.reviewForm = {
        role: this.reviewRoles[0] || "",
        reviewComment: "",
      };
      this.approveDialog = true;
    },
    openRejectDialog(item) {
      this.reviewTarget = item;
      this.reviewForm = {
        role: "",
        reviewComment: "",
      };
      this.rejectDialog = true;
    },
    openDetailDialog(item) {
      this.detailTarget = item || {};
      this.detailDialog = true;
    },
    async approveApplication() {
      if (!this.reviewForm.role) {
        this.$toast.error("請選擇角色");
        return;
      }
      try {
        await this.axios.post(
          `/dashboard/user/applications/${this.reviewTarget._id}/review`,
          {
            action: "approve",
            role: this.reviewForm.role,
            reviewComment: this.reviewForm.reviewComment,
          }
        );
        this.$toast.success("已核准申請並寄出通知信");
        this.approveDialog = false;
        await this.load();
        await this.loadApplications();
      } catch (error) {
        this.$toast.error(error.response?.data?.errMsg || "核准失敗");
      }
    },
    async rejectApplication() {
      if (!this.reviewForm.reviewComment) {
        this.$toast.error("請填寫退件原因");
        return;
      }
      try {
        await this.axios.post(
          `/dashboard/user/applications/${this.reviewTarget._id}/review`,
          {
            action: "reject",
            reviewComment: this.reviewForm.reviewComment,
          }
        );
        this.$toast.success("已退件並寄出通知信");
        this.rejectDialog = false;
        await this.loadApplications();
      } catch (error) {
        this.$toast.error(error.response?.data?.errMsg || "退件失敗");
      }
    },
    async update(item) {
      this.selected = item;
      this.dialog = true;
    },
    async create() {
      this.selected = {};
      this.dialog = true;
    },
  },
  watch: {},
};
</script>

<style></style>
